<template>
  <section v-if="dashboardLoading || items.length" class="cso-panel" :class="scope.clsPrefix">
    <div class="panel-head">
      <span class="panel-title">供应链战略推进</span>
    </div>
    <div class="panel-body">
      <div v-if="dashboardLoading" class="progress-container">
        <div class="progress-item skeleton-item" v-for="n in 5" :key="n">
          <span class="skeleton-line name"></span>
          <div class="skeleton-line bar"></div>
          <span class="skeleton-line percent"></span>
          <span class="skeleton-line tag"></span>
        </div>
      </div>
      <div v-else class="progress-container">
        <div class="progress-item" v-for="item in items" :key="item.label">
          <span class="item-name">{{ item.label }}</span>
          <div class="progress-bar-wrapper">
            <div class="bar-fill" :class="item.statusClass" :style="{ width: item.progress + '%' }"></div>
          </div>
          <span class="item-value">{{ item.progress }}%</span>
          <span class="item-status" :class="item.statusClass">{{ item.tag }}</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { REGION, groupByRegion, parseMeta } from '../utils/externalMetrics'

// direction → 状态词（externalMetrics 无 status 字段，由环比方向推断）
const DIRECTION_STATUS = {
  up: { tag: '领先', statusClass: 'status-leading' },
  flat: { tag: '正常', statusClass: 'status-normal' },
  down: { tag: '预警', statusClass: 'status-warning' }
}

export default {
  name: 'CsoStrategyProgress',
  props: {
    dashboardData: {
      type: Object,
      default: () => ({})
    },
    dashboardLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      scope: {
        clsPrefix: 'rvc-cosmoplat-workspace-cso-strategy-progress'
      }
    }
  },
  computed: {
    items() {
      const data = this.dashboardData || {}
      const ext = data.externalMetrics
      if (!ext || !Array.isArray(ext.data)) return []
      const group = groupByRegion(ext.data)[REGION.STRATEGY_PROGRESS]
      if (!group || !group.length) return []
      return group.map((item) => this.buildItem(item))
    }
  },
  methods: {
    buildItem(item) {
      return {
        label: item.display_name || '',
        progress: this.normalizeRate(item.metric_value),
        ...this.statusOf(item)
      }
    },
    normalizeRate(value) {
      const num = Number(value)
      if (!Number.isFinite(num)) return 0
      // 接口可能返回 0~1 的小数比例（如 0.85），此时放大为百分比；0~100 直接使用
      const pct = num > 0 && num <= 1 ? num * 100 : num
      return Math.max(0, Math.min(100, Number(pct.toFixed(2))))
    },
    statusOf(item) {
      const meta = parseMeta(item.meta_json)
      const direction = meta.mom ? meta.mom.direction : 'flat'
      return DIRECTION_STATUS[direction] || DIRECTION_STATUS.flat
    }
  }
}
</script>

<style lang="scss" scoped>
$class-prefix: 'rvc-cosmoplat-workspace-cso-strategy-progress';

.#{$class-prefix} {
  min-height: 0;
  display: flex;
  flex-direction: column;

  .panel-head {
    background: transparent;
    color: var(--text, #1f2937);
    padding: 12px 18px 6px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .panel-title {
    font-size: 14px;
    font-weight: 600;
    color: rgb(30, 34, 38);
    line-height: 20px;
  }
  .panel-body {
    padding: 8px 18px 16px;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 0;
  }
  .progress-container {
    display: flex;
    flex-direction: column;
    gap: 18px;
    min-width: 0;
  }
  .progress-item {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }
  .item-name {
    // 固定 4 字宽并允许换行：所有名称占同宽列，进度条左侧对齐到同一竖线
    flex: 0 0 48px;
    width: 48px;
    font-family: PingFangSC-Medium;
    font-weight: 600;
    font-size: 12px;
    color: #333333;
    line-height: 1.4;
    overflow-wrap: anywhere;
    word-break: break-all;
  }
  .progress-bar-wrapper {
    flex: 1;
    min-width: 0;
    height: 6px;
    background: #f0f0f0;
    border-radius: 3px;
    overflow: hidden;
  }
  .bar-fill {
    height: 6px;
    border-radius: 3px;
    transition: width 0.5s ease;
    // 进度条按状态着色，与右侧状态标签配色一致
    &.status-leading {
      background: linear-gradient(90deg, #73B950 0%, #98D17C 100%);
    }
    &.status-normal {
      background: linear-gradient(90deg, #4A5FF3 0%, #4E86FF 100%);
    }
    &.status-warning {
      background: linear-gradient(90deg, #faad14 0%, #FAD37E 100%);
    }
    &.status-danger {
      background: linear-gradient(90deg, #F44F4F 0%, #F87B7B 100%);
    }
  }
  .item-value {
    flex-shrink: 0;
    font-family: PingFangSC-Regular;
    font-size: 14px;
    color: #333333;
    font-weight: 400;
    min-width: 44px;
    text-align: right;
  }
  .item-status {
    flex-shrink: 0;
    font-size: 12px;
    font-family: PingFangSC-Regular;
    font-weight: 400;
    padding: 2px 8px;
    border-radius: 10px;
    min-width: 36px;
    text-align: center;
    &.status-leading {
      color: #73B950;
      background: #F1F8ED;
    }
    &.status-normal {
      color: #4A5FF3;
      background: #EDF1FB;
    }
    &.status-warning {
      color: #faad14;
      background: #FFFBE6;
    }
    &.status-danger {
      color: #F44F4F;
      background: #FEEDED;
    }
  }

  // 骨架
  .skeleton-line {
    height: 12px;
    border-radius: 4px;
    background: linear-gradient(90deg, #eef3fb 25%, #f8fbff 37%, #eef3fb 63%);
    background-size: 400% 100%;
    animation: cso-sp-skeleton-loading 1.2s ease-in-out infinite;
    &.name {
      width: 48px;
      flex-shrink: 0;
    }
    &.bar {
      flex: 1;
      height: 6px;
      min-width: 0;
    }
    &.percent {
      width: 36px;
      flex-shrink: 0;
    }
    &.tag {
      width: 36px;
      height: 18px;
      flex-shrink: 0;
    }
  }
  @keyframes cso-sp-skeleton-loading {
    0% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0 50%;
    }
  }
}
</style>
